/**
 * MoM (Mixture of Minds) Router
 * Routes LLM queries to specialized models based on task type detection
 */

import type { LLMMessage, LLMConfig } from "./llmService";

/**
 * Task keywords for routing decisions
 * Presence of these keywords in user prompt determines routing
 */
const TASK_KEYWORDS = {
  // DeepSeek-R1: Logic, math, proofs, analysis, reasoning
  reasoning: [
    "prove",
    "calculate",
    "compute",
    "analyze",
    "deduce",
    "verify",
    "logical",
    "math",
    "algebra",
    "calculus",
    "theorem",
    "proof",
    "equation",
    "solve",
    "formula",
    "determine",
    "evaluate",
    "assess",
    "reasoning",
    "derivation",
    "inference",
  ],

  // Qwen-Coder: Code generation, refactoring, debugging
  coding: [
    "implement",
    "refactor",
    "debug",
    "patch",
    "code",
    "function",
    "class",
    "interface",
    "type",
    "write",
    "generate",
    "create code",
    "fix code",
    "api",
    "endpoint",
    "algorithm",
    "data structure",
    "recursive",
    "syntax",
    "compile",
    "typescript",
    "javascript",
    "python",
    "rust",
    "go",
    "programming",
  ],

  // GLM-4.7: General orchestration (default)
  general: [], // Empty array - defaults to GLM
};

/**
 * Task type classification result
 */
export interface ModelRoute {
  provider: "glm" | "deepseek" | "qwen";
  confidence: number; // 0-1, higher means more confident in routing
  reason: string;
}

/**
 * Analyze prompt and determine optimal model routing
 *
 * Priority order:
 * 1. Coding keywords → Qwen-Coder-Plus
 * 2. Reasoning keywords → DeepSeek-R1
 * 3. Everything else → GLM-4.7 (default)
 *
 * Confidence is based on keyword density in prompt
 */
export function routeLLM(prompt: string): ModelRoute {
  const lowerPrompt = prompt.toLowerCase();
  const words = lowerPrompt.split(/\s+/);
  const totalWords = Math.max(words.length, 1);

  // Count matches for each category
  const reasoningMatches = TASK_KEYWORDS.reasoning.filter((kw) =>
    lowerPrompt.includes(kw.toLowerCase())
  ).length;

  const codingMatches = TASK_KEYWORDS.coding.filter((kw) =>
    lowerPrompt.includes(kw.toLowerCase())
  ).length;

  // Calculate confidence based on match density
  const reasoningDensity = reasoningMatches / totalWords;
  const codingDensity = codingMatches / totalWords;

  // Determine route
  // Coding has highest priority (code generation is specific)
  if (codingMatches > 0) {
    return {
      provider: "qwen",
      confidence: Math.min(codingDensity * 10, 1), // Cap at 1.0
      reason: `Detected coding intent (${codingMatches} matches)`,
    };
  }

  // Reasoning has second priority
  if (reasoningMatches > 0) {
    return {
      provider: "deepseek",
      confidence: Math.min(reasoningDensity * 5, 1),
      reason: `Detected reasoning intent (${reasoningMatches} matches)`,
    };
  }

  // Default to GLM for general tasks
  return {
    provider: "glm",
    confidence: 0.3, // Low confidence - general fallback
    reason: "No specialized intent detected, using general model",
  };
}

/**
 * Get LLM config based on routing decision
 */
export function getRoutedConfig(
  prompt: string,
  configs: LLMConfig[]
): LLMConfig | null {
  const route = routeLLM(prompt);

  // Find config for routed provider
  return configs.find((c) => c.provider === route.provider) || null;
}

/**
 * Parallel query comparison
 * Sends same prompt to multiple models and selects best response
 * Currently experimental - not enabled by default
 */
export interface ParallelResult {
  provider: string;
  response: string;
  tokensUsed?: number;
  latency: number; // ms
  score?: number;
}

export async function queryParallel(
  messages: LLMMessage[],
  configs: LLMConfig[],
  callFn: (messages: LLMMessage[], config: LLMConfig) => Promise<any>
): Promise<ParallelResult[] | null> {
  if (configs.length < 2) return null;

  const startTime = Date.now();
  const results: any[] = await Promise.allSettled(
    configs.map(async (config) => {
      const start = Date.now();
      try {
        const response = await callFn(messages, config);
        return {
          provider: config.provider,
          response: response.content,
          tokensUsed: response.tokensUsed || 0,
          latency: Date.now() - start,
        };
      } catch {
        return null;
      }
    })
  );

  const successful = results.filter(
    (r): r is { status: "fulfilled"; value: ParallelResult | null } =>
      r.status === "fulfilled" && r.value !== null
  );

  return successful.map((r) => r.value as ParallelResult);
}

/**
 * Select best response from parallel queries
 * Heuristics: favor tokens (more detail), lower latency, provider preference
 */
export function selectBestResponse(
  results: ParallelResult[]
): ParallelResult | null {
  if (results.length === 0) return null;
  if (results.length === 1) return results[0];

  // Score each response
  // Criteria: More tokens = more detailed, less latency = faster
  const scored = results.map((r) => {
    let score = 0;

    // Token count (up to 2048 is good baseline)
    score += Math.min((r.tokensUsed || 0) / 2048, 1) * 50;

    // Latency (lower is better, 1000ms is baseline)
    score += Math.max(0, 1 - r.latency / 1000) * 30;

    // Provider preference (GLM gets slight priority for general tasks)
    if (r.provider === "glm") score += 10;
    if (r.provider === "qwen") score += 5;

    return { ...r, score };
  });

  // Return highest scored
  scored.sort((a, b) => (b.score || 0) - (a.score || 0));
  return scored[0];
}

/**
 * Fallback chain execution
 * Tries models in order: routed → GLM → DeepSeek → Qwen
 */
export async function queryWithFallback(
  messages: LLMMessage[],
  configs: LLMConfig[],
  callFn: (messages: LLMMessage[], config: LLMConfig) => Promise<any>,
  enableFallback: boolean = true
): Promise<{ response: any; provider: string; attempts: string[] }> {
  const lastUserMsg = messages.filter((m) => m.role === "user").pop();
  const prompt = lastUserMsg?.content || "";

  // Try routed model first
  const route = routeLLM(prompt);
  const attempted: string[] = [];

  // Try routed provider
  const routedConfig = configs.find((c) => c.provider === route.provider);
  if (routedConfig) {
    attempted.push(route.provider);
    try {
      const response = await callFn(messages, routedConfig);
      return { response, provider: route.provider, attempts: attempted };
    } catch (error) {
      console.log(`Routed model ${route.provider} failed:`, error);
    }
  }

  if (!enableFallback) {
    throw new Error(
      `Routed model ${route.provider} failed and fallback is disabled`
    );
  }

  // Fallback order: GLM → DeepSeek → Qwen
  const fallbackOrder: ("glm" | "deepseek" | "qwen")[] = ["glm", "deepseek", "qwen"];

  for (const provider of fallbackOrder) {
    if (attempted.includes(provider)) continue;

    const config = configs.find((c) => c.provider === provider);
    if (!config) continue;

    attempted.push(provider);
    try {
      const response = await callFn(messages, config);
      return { response, provider, attempts: attempted };
    } catch (error) {
      console.log(`Fallback model ${provider} failed:`, error);
    }
  }

  throw new Error(
    `All LLM providers failed. Attempted: ${attempted.join(", ")}`
  );
}

/**
 * Enhanced routing info for logging/debugging
 */
export interface RoutingDecision {
  route: ModelRoute;
  attemptedProviders: string[];
  successProvider: string | null;
  error?: string;
  latency: number;
}

/**
 * Track routing decisions for analytics
 */
const routingHistory: RoutingDecision[] = [];

export function recordRouting(decision: RoutingDecision) {
  routingHistory.push(decision);
  // Keep last 1000 decisions
  if (routingHistory.length > 1000) {
    routingHistory.shift();
  }
}

export function getRoutingStats() {
  const byProvider = routingHistory.reduce(
    (acc, r) => {
      acc[r.successProvider || "failed"] = (acc[r.successProvider || "failed"] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const byModel = routingHistory.reduce(
    (acc, r) => {
      const model = r.route.provider;
      acc[model] = (acc[model] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return {
    totalRoutings: routingHistory.length,
    byProvider,
    byModel,
    successRate:
      routingHistory.length > 0
        ? routingHistory.filter((r) => r.successProvider !== null).length /
          routingHistory.length
        : 0,
  };
}
